import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import moment from 'moment';
import { Box, Grid, Typography } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';

import PageContainer from '../../../components/layout/PageContainer';
import {
  FBoxContainer,
  FHAutocomplete,
  FHButton,
  FHInputField,
  FProgressBar,
} from '../../../components/base';
import { providerActions, treatmentPlanActions } from '../../../store';
import { getUtcOffset } from '../../../utils/common';
import { isTreatmentPlanReassignmentCandidate, reassignmentProviderLabel } from './utils';

import type { RootState } from '../../../store';
import type { ChangeEvent } from 'react';
import type { IOption, IProvider } from '../../../utils/types';

function ReassignTreatmentPlan() {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { loading: treatmentPlanLoading } = useSelector((state: RootState) => state.treatmentPlans);
  const { list: providers = [], loading: providerLoading } = useSelector(
    (state: RootState) => state.providers,
  );
  const [tpId, setTpId] = useState<string>('');
  const [selectedProvider, setSelectedProvider] = useState<IProvider | null>(null);

  useEffect(() => {
    dispatch(providerActions.getList({ getAccessTokenSilently, include_roles: true }));
  }, [dispatch, getAccessTokenSilently]);

  const handleSubmit = () => {
    if (isNaN(Number(tpId))) {
      toast.error('Treatment plan ID must be a number', {
        toastId: 'tp-id-status-error',
      });
      return;
    }
    if (tpId && selectedProvider && selectedProvider.id) {
      dispatch(
        treatmentPlanActions.reassignTreatmentPlan({
          treatmentPlanId: tpId.trim(),
          data: {
            provider_id: selectedProvider.id,
            utc_offset_date_assigned: getUtcOffset(moment(new Date())),
          },
          getAccessTokenSilently,
        }),
      );
    }
  };
  return (
    <PageContainer>
      {(providerLoading || treatmentPlanLoading) && <FProgressBar />}
      <Box sx={{ mt: 3 }}>
        <FBoxContainer
          dataTestId="reassignTreatmentPlan"
          sx={{ borderRadius: 2, width: '100%', boxShadow: 'none' }}
        >
          <Grid container>
            <Grid item xs={12} md={12} lg={8}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Box ml={1} sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                  <Typography textAlign={'left'} variant="h4">
                    Reassign treatment plan
                  </Typography>
                </Box>
                <Box
                  sx={{ width: '100%', borderBottom: 1, borderColor: 'divider', mt: 1, mb: 2 }}
                />
                <Grid container p={0.5} spacing={1}>
                  <Grid item xs={2.5}>
                    <Typography textAlign={'left'}>Treatment Plan ID:</Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <FHInputField
                      inputProps={{ 'data-testid': 'tpIdInput' }}
                      inputHeight={40}
                      sx={{ width: '100%', boxShadow: 'none' }}
                      value={tpId}
                      onChange={(e) => setTpId(e.target.value)}
                    />
                  </Grid>
                </Grid>
                <Grid container p={0.5} spacing={1}>
                  <Grid item xs={2.5}>
                    <Typography textAlign={'left'}>Provider:</Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <FHAutocomplete
                      inputHeight={40}
                      data-testid="tpChangeProvider"
                      filterSelectedOptions
                      sx={{ width: '100%', boxShadow: 'none' }}
                      placeholder="Select Provider"
                      isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                      options={providers
                        .filter(isTreatmentPlanReassignmentCandidate)
                        .map((provider: IProvider) => ({
                          id: provider.id,
                          label: reassignmentProviderLabel(provider),
                        }))}
                      renderOption={(props: any, option: IOption) => {
                        return (
                          <li {...props} key={option.id}>
                            {option.label}
                          </li>
                        );
                      }}
                      value={selectedProvider}
                      onChange={(
                        event: ChangeEvent<HTMLSelectElement>,
                        newValue: IProvider | null,
                      ) => {
                        setSelectedProvider(newValue);
                      }}
                    />
                  </Grid>
                </Grid>

                <FHButton
                  data-testid="changeTpButton"
                  variant="contained"
                  sx={{ mt: 2, width: '200px' }}
                  onClick={handleSubmit}
                  disabled={treatmentPlanLoading || !tpId || !selectedProvider}
                >
                  Change provider
                </FHButton>
              </Box>
            </Grid>
          </Grid>
        </FBoxContainer>
      </Box>
    </PageContainer>
  );
}

export default ReassignTreatmentPlan;
